import type { ReactNode } from 'react'
import { motion, useReducedMotion } from 'framer-motion'

/** Page frame: content sits between two vertical rails. */
export function Rails({ children }: { children: ReactNode }) {
  return <div className="relative mx-auto w-full max-w-[1200px] border-x border-white/[0.07]">{children}</div>
}

/** Small plus mark where a section border meets a rail. */
function Crosshair({ side }: { side: 'left' | 'right' }) {
  return (
    <svg aria-hidden="true" width="11" height="11" viewBox="0 0 11 11"
      className={`absolute top-0 -translate-y-1/2 ${side === 'left' ? 'left-0 -translate-x-1/2' : 'right-0 translate-x-1/2'}`}>
      <path d="M5.5 0V11M0 5.5H11" stroke="#64748b" strokeWidth="1" />
    </svg>
  )
}

/** Fades its children up once when they scroll into view. Static under reduced motion. */
export function Reveal({ children, className = '', delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const reduced = useReducedMotion()
  if (reduced) return <div className={className}>{children}</div>
  return (
    <motion.div className={className} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '0px 0px -80px 0px' }} transition={{ duration: 0.4, ease: 'easeOut', delay }}>
      {children}
    </motion.div>
  )
}

/** One landing section: full-width top border with crosshairs at the rails. */
export default function Section({ id, className = '', children, labelledBy }: { id?: string; className?: string; children: ReactNode; labelledBy?: string }) {
  return (
    <section id={id} aria-labelledby={labelledBy} className={`relative scroll-mt-14 border-t border-white/[0.07] ${className}`}>
      <Crosshair side="left" />
      <Crosshair side="right" />
      {children}
    </section>
  )
}

/** Mono eyebrow plus a two-tone headline: bright lead phrase, muted continuation. */
export function SectionHeading({ id, label, lead, rest, className = '' }: { id: string; label: string; lead: string; rest: string; className?: string }) {
  return (
    <Reveal className={className}>
      <p className="font-mono text-[11px] uppercase tracking-widest text-cyan-400/80">{label}</p>
      <h2 id={id} className="mt-4 max-w-4xl font-display text-4xl font-semibold leading-[1.05] tracking-[-0.025em] text-slate-50 md:text-5xl">
        {lead} <span className="text-slate-500">{rest}</span>
      </h2>
    </Reveal>
  )
}
